import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Stack,
  CardActions,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Add, Edit, Delete, Search, FilterList, Refresh, FilterAlt, CheckCircle, HourglassEmpty, Cancel, TrendingUp, Inventory } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProducts, deleteProduct, clearError, clearCurrentProduct } from '../store/slices/productSlice';
import { getDashboardStats } from '../store/slices/dashboardSlice';
import { getCategories } from '../store/slices/categorySlice';

const Products = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { products, isLoading, error } = useSelector((state) => state.products);
  const { stats } = useSelector((state) => state.dashboard);
  const { categories } = useSelector((state) => state.categories || {});
  const [imageErrors, setImageErrors] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Enhanced state for pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch products and categories only once on mount
  useEffect(() => {
    dispatch(getProducts({ limit: 1000 }));
    dispatch(getDashboardStats());
    dispatch(getCategories());

    // Cleanup on unmount
    return () => {
      dispatch(clearError());
      dispatch(clearCurrentProduct());
    };
  }, [dispatch]);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Refresh products handler
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await dispatch(getProducts({ limit: 1000 }));
    } catch (error) {
      console.error('Error refreshing products:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to get category display name (parent > subcategory)
  const getCategoryDisplayName = useCallback((product) => {
    if (!product.category) return 'N/A';

    const category = product.category;

    // Strategy:
    // 1. If category is fully populated with parentCategory object -> Use it.
    // 2. If category has parentCategory ID -> Find parent in global categories list.
    // 3. If category is just an ID -> Find it in global categories list.

    // Case A: Product.category is populated (Object)
    if (typeof category === 'object' && category !== null) {
      const catName = category.name || 'Unknown';
      let parentName = '';

      // Check if parentCategory is populated inside the category object
      if (category.parentCategory && typeof category.parentCategory === 'object' && category.parentCategory.name) {
        parentName = category.parentCategory.name;
      }
      // Check if parentCategory is just an ID, look it up
      else if (category.parentCategory) {
        const parentId = category.parentCategory;
        const parent = categories.find(c => c._id === parentId || c._id === parentId.toString());
        if (parent) parentName = parent.name;
      }

      return parentName ? `${parentName} > ${catName}` : catName;
    }

    // Case B: Product.category is just an ID
    if (typeof category === 'string' || typeof category === 'number') {
      const foundCat = categories.find(c => c._id === category || c._id === category.toString());
      if (foundCat) {
        let parentName = '';
        if (foundCat.parentCategory) {
          const parentId = foundCat.parentCategory._id || foundCat.parentCategory;
          const parent = categories.find(c => c._id === parentId || c._id === parentId.toString());
          if (parent) parentName = parent.name;
        }
        return parentName ? `${parentName} > ${foundCat.name}` : foundCat.name;
      }
    }

    return 'N/A';
  }, [categories]);

  // Filter products based on search term, category, and status
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = categoryFilter ? (
        product.category?._id === categoryFilter ||
        product.category === categoryFilter ||
        (typeof product.category === 'object' && product.category._id === categoryFilter)
      ) : true;
      
      let matchesStatus = true;
      switch (statusFilter) {
        case 'all':
          matchesStatus = true;
          break;
        case 'active':
          matchesStatus = product.isActive !== false;
          break;
        case 'inactive':
          matchesStatus = product.isActive === false;
          break;
        case 'pending':
          matchesStatus = product.verificationStatus === 'pending';
          break;
        case 'trending':
          matchesStatus = product.isTrending === true;
          break;
        default:
          matchesStatus = true;
      }
      
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, searchTerm, categoryFilter, statusFilter]);

  // Pagination for products
  const paginatedProducts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, page, rowsPerPage]);

  // Memoize products to prevent unnecessary re-renders (updated to use paginatedProducts)
  const memoizedProducts = useMemo(() => {
    return paginatedProducts || [];
  }, [paginatedProducts]);

  // Handle image errors
  const handleImageError = useCallback((productId) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  }, []);

  // Get image URL helper
  const getImageUrl = useCallback((product) => {
    if (!product.images || !product.images[0]) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjZjVmNWY1Ii8+PHR5cGUgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90eXBlPjwvc3ZnPg==';
    }

    const imagePath = product.images[0];
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    const apiBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://xaintro-backend.onrender.com';
    const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${apiBaseUrl}${normalizedPath}`;
  }, []);

  const handleDelete = useCallback((id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      dispatch(deleteProduct(id)).then(() => {
        // Refresh products after deletion
        dispatch(getProducts({ limit: 1000 }));
      });
    }
  }, [dispatch]);

  if (isLoading || isRefreshing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Tab Navigation - Inline Layout */}
      <Paper sx={{ mb: 3, borderRadius: 2, p: 2 }}>
        <Grid container spacing={2}>
          {[
            { id: 'all', icon: <Inventory />, label: "All Products", color: "#ff2852" },
            { id: 'active', icon: <CheckCircle />, label: "Active", color: "#10b981" },
            { id: 'pending', icon: <HourglassEmpty />, label: "Pending", color: "#f59e0b" },
            { id: 'inactive', icon: <Cancel />, label: "Inactive", color: "#ef4444" },
            { id: 'trending', icon: <TrendingUp />, label: "Trending", color: "#8b5cf6" }
          ].map((tab) => (
            <Grid item xs={6} sm={6} md={6} lg={6} key={tab.id}>
              <Paper
                onClick={() => setStatusFilter(tab.id === 'all' ? 'all' : tab.id)}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: (statusFilter === tab.id || (tab.id === 'all' && statusFilter === 'all')) ? `2px solid ${tab.color}` : '1px solid #e0e0e0',
                  backgroundColor: (statusFilter === tab.id || (tab.id === 'all' && statusFilter === 'all')) ? `${tab.color}10` : '#ffffff',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${tab.color}15`,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ color: (statusFilter === tab.id || (tab.id === 'all' && statusFilter === 'all')) ? tab.color : '#666', mb: 1, fontSize: '1.5rem' }}>
                  {tab.icon}
                </Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600}
                  sx={{ 
                    color: (statusFilter === tab.id || (tab.id === 'all' && statusFilter === 'all')) ? tab.color : '#666',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }}
                >
                  {tab.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Header with Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          sx={{
            color: '#000000',
            fontWeight: 700,
            pb: 2,
            borderBottom: '2px solid #ff2852',
          }}
        >
          Products
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/gift-boxes')}
            sx={{
              borderColor: '#ff2852',
              color: '#000000',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#5A189A',
                backgroundColor: 'rgba(123, 44, 191, 0.1)',
              },
            }}
          >
            Gift Boxes
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/products/new')}
            sx={{
              backgroundColor: '#ff2852',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#5A189A',
              },
            }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Product Statistics */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3} mb={3}>
        <Paper sx={{
          p: 2,
          flex: 1,
          textAlign: 'center',
          borderTop: '4px solid #ff2852',
          minHeight: 100,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <Typography variant="subtitle2" color="text.secondary">Total Products</Typography>
          <Typography variant="h4" fontWeight="bold">{products?.length || 0}</Typography>
        </Paper>
        <Paper sx={{
          p: 2,
          flex: 1,
          textAlign: 'center',
          borderTop: '4px solid #4caf50',
          minHeight: 100,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <Typography variant="subtitle2" color="text.secondary">Active Products</Typography>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {products?.filter(p => p.isActive !== false).length || 0}
          </Typography>
        </Paper>
        <Paper sx={{
          p: 2,
          flex: 1,
          textAlign: 'center',
          borderTop: '4px solid #9e9e9e',
          minHeight: 100,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <Typography variant="subtitle2" color="text.secondary">Inactive Products</Typography>
          <Typography variant="h4" fontWeight="bold" color="text.secondary">
            {products?.filter(p => p.isActive === false).length || 0}
          </Typography>
        </Paper>
      </Box>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} alignItems="center">
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search products by name or brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 2 }}
          />
          <TextField
            select
            fullWidth
            variant="outlined"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Filter by Category"
            sx={{ flex: 1 }}
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category._id} value={category._id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              startAdornment={<FilterAlt sx={{ mr: 1, fontSize: 18 }} />}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <IconButton 
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{ 
              border: '1px solid #ddd', 
              borderRadius: 1,
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            <Refresh sx={{ 
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }} />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
            Showing {memoizedProducts.length} of {filteredProducts.length} products
          </Typography>
        </Box>
      </Paper>

      {isMobile ? (
        <Grid container spacing={2}>
          {memoizedProducts.length > 0 ? (
            memoizedProducts.map((product) => {
              const imageUrl = imageErrors[product._id]
                ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjZjVmNWY1Ii8+PHR5cGUgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90eXBlPjwvc3ZnPg=='
                : getImageUrl(product);

              return (
                <Grid item xs={12} key={product._id}>
                  <Card sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}>
                    <Box display="flex" p={2} alignItems="center">
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 1,
                          border: '1px solid #ff2852',
                          overflow: 'hidden',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mr: 2,
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={product.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                          onError={() => handleImageError(product._id)}
                          loading="lazy"
                        />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600, mb: 0.5 }}>
                          {product.name}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap" gap={0.5}>
                          <Chip
                            label={product.isActive ? 'Active' : 'Inactive'}
                            sx={{
                              backgroundColor: product.isActive ? '#4caf50' : '#9e9e9e',
                              color: '#ffffff',
                              fontWeight: 600,
                              height: 24,
                              fontSize: '0.75rem',
                            }}
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {getCategoryDisplayName(product) || 'N/A'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ color: '#666666' }}>
                            {product.brand}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666' }}>
                            Stock: {product.stock}
                          </Typography>
                        </Stack>
                        <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 700, mt: 0.5 }}>
                          PKR {product.price || product.basePrice || '0.00'}
                        </Typography>
                      </Box>
                    </Box>
                    <CardActions sx={{ borderTop: '1px solid #f0f0f0', justifyContent: 'flex-end', p: 1 }}>
                      <IconButton
                        onClick={() => navigate(`/products/${product._id}/edit`)}
                        sx={{
                          color: '#ff2852',
                          '&:hover': {
                            backgroundColor: 'rgba(123, 44, 191, 0.1)',
                          },
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(product._id)}
                        sx={{
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center', color: '#666' }}>
                <Typography variant="h6" gutterBottom>
                  No products found matching your criteria
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || categoryFilter || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filters' 
                    : 'No products available in the database'}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Paper sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#fafafa',
                }}>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                    width: 70,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#fafafa',
                    zIndex: 10,
                  }}>Image</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Name</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Category</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Brand</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Price</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Stock</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Status</TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#000000',
                    borderBottom: '2px solid #ff2852',
                  }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memoizedProducts.length > 0 ? (
                  memoizedProducts.map((product) => {
                    const imageUrl = imageErrors[product._id]
                      ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjZjVmNWY1Ii8+PHR5cGUgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90eXBlPjwvc3ZnPg=='
                      : getImageUrl(product);

                    return (
                      <TableRow
                        key={product._id}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#fffef5',
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell sx={{ width: 70 }}>
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: 1,
                              border: '1px solid #ff2852',
                              overflow: 'hidden',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={imageUrl}
                              alt={product.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              onError={() => handleImageError(product._id)}
                              loading="lazy"
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#000000', fontWeight: 500 }}>{product.name}</TableCell>
                        <TableCell sx={{ color: '#666666' }}>
                          {getCategoryDisplayName(product) || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: '#666666' }}>{product.brand}</TableCell>
                        <TableCell sx={{ color: '#000000', fontWeight: 600 }}>PKR {product.price || product.basePrice || '0.00'}</TableCell>
                        <TableCell sx={{ color: '#666666' }}>{product.stock}</TableCell>
                        <TableCell>
                          <Chip
                            label={product.isActive ? 'Active' : 'Inactive'}
                            sx={{
                              backgroundColor: product.isActive ? '#4caf50' : '#9e9e9e',
                              color: '#ffffff',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => navigate(`/products/${product._id}/edit`)}
                            sx={{
                              color: '#ff2852',
                              '&:hover': {
                                backgroundColor: 'rgba(123, 44, 191, 0.1)',
                              },
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(product._id)}
                            sx={{
                              color: '#f44336',
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="h6" sx={{ py: 3, color: '#666' }} gutterBottom>
                        No products found matching your criteria
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || categoryFilter || statusFilter !== 'all' 
                          ? 'Try adjusting your search or filters' 
                          : 'No products available in the database'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination for Desktop */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: '1px solid #eee', borderRadius: 2, borderTop: 'none' }}
          />
        </Paper>
      )}
      
      {/* Pagination for Mobile (shown when mobile view is active) */}
      {isMobile && filteredProducts.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: '1px solid #eee', borderRadius: 2 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Products;